import React, { useEffect, useMemo, useState } from "react";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { AZUL, BORDA, CINZA_BOTAO, FONTE, FUNDO_CARTAO, FUNDO_SUAVE, NAVY, NAVY_FUNDO, SUAVE, TEXTO_SOBRE_COR, VERDE_TEXTO, estilos } from "../estilos/tokens";
import { CartaoResumo } from "./painelOperadoras";
import EstadoRequisicao from "../componentes/EstadoRequisicao";
import { useFiltroAba } from "../contexto/filtroHome";
import { useRequisicao } from "../hooks/useRequisicao";
import { useAlturaDisponivel } from "../hooks/useAlturaDisponivel";
import { useMedidasElemento } from "../hooks/useLarguraElemento";
import { FiltroHome, buscarPrevisoes, PrevisaoMes } from "../../../../services/home";
import Grafico from "../componentes/Grafico";
import { alturaGraficoPrevisoes, barrasDoMes, graficoEstreito, montaOpcoesPrevisoes } from "./graficoPrevisoes";
import { formatterCurrency } from "../../../../utils/formatacaoNumeros";
import { usePaletaAtiva } from "../../../../styles/theme/usePaletaAtiva";
import { textoJanelaPrevisoes } from "./janelaPrevisoes";
import { CABECALHO_PREVISOES_CSV, linhasPrevisoesCsv } from "./previsoesCsv";
import { baixarCsv, montarCsv } from "../componentes/csv";
import { colunasAbasMes } from "./abasMesPrevisoes";

const SEM_MESES: PrevisaoMes[] = [];

const estilosAba = {
  tituloBloco: {
    fontFamily: FONTE,
    fontSize: "clamp(21px, 3.6vw, 26px)",
    fontWeight: 700,
    color: NAVY,
  },
  subtituloBloco: {
    fontFamily: FONTE,
    fontSize: "clamp(15px, 2.3vw, 19px)",
    fontWeight: 500,
    color: AZUL,
    marginBottom: 12,
  },
  janela: {
    fontFamily: FONTE,
    fontSize: 13,
    color: SUAVE,
    lineHeight: 1.5,
    marginBottom: 22,
  },
};

const estiloPainelGrafico = {
  border: `1px solid ${BORDA}`,
  borderRadius: 12,
  padding: "clamp(14px, 2vw, 22px)",
  display: "flex",
  flexDirection: "column" as "column",
  gap: 18,
  minWidth: 0,
  flex: 1,
};

const estiloAbasMes = (colunas: number) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))`,
  gap: 4,
  padding: 4,
  borderRadius: 10,
  background: FUNDO_SUAVE,
});

const estiloAbaMes = (ativa: boolean) => ({
  fontFamily: FONTE,
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: "0.06em",
  padding: "7px 4px",
  borderRadius: 7,
  border: 0,
  background: ativa ? FUNDO_CARTAO : "transparent",
  color: ativa ? AZUL : SUAVE,
  boxShadow: ativa ? "0 1px 3px rgba(17, 35, 90, 0.15)" : "none",
  cursor: "pointer",
});

const TotalDoMes = ({ valor }: { valor: number }) => (
  <div style={{ fontFamily: FONTE, padding: "8px 14px", borderRadius: 8, background: FUNDO_SUAVE, minWidth: 0, alignSelf: "flex-start" }}>
    <div style={{ fontSize: 12, color: SUAVE }}>Total previsto no mês</div>
    <div data-teste="total-mes" style={{ fontSize: 15, fontWeight: 700, color: VERDE_TEXTO, whiteSpace: "nowrap" }}>
      {formatterCurrency(valor)}
    </div>
  </div>
);

const Previsoes = () => {
  const filtro = useFiltroAba("PREVISAO");
  const { dados, carregando, erro, recarregar } = useRequisicao(
    () => buscarPrevisoes(filtro as FiltroHome),
    [filtro],
    Boolean(filtro)
  );
  const { referencia: refCartao, altura: alturaDisponivel } = useAlturaDisponivel();
  const { referencia: refAreaGrafico, altura: alturaAreaGrafico, largura: larguraGrafico } = useMedidasElemento();
  const largo = useMediaQuery(useTheme().breakpoints.up("md"));

  const vazio = !!dados && dados.meses.length === 0 && !dados.mesCorrente;

  const meses = dados ? dados.meses : SEM_MESES;
  const [indiceMes, setIndiceMes] = useState(0);
  useEffect(() => setIndiceMes(0), [meses]);
  const mesSelecionado = meses[indiceMes] || meses[0];

  const barras = useMemo(() => (mesSelecionado ? barrasDoMes(mesSelecionado) : []), [mesSelecionado]);
  const alturaMinimaGrafico = alturaGraficoPrevisoes(barras.length);
  const alturaGrafico = largo ? Math.max(alturaMinimaGrafico, Math.floor(alturaAreaGrafico)) : alturaMinimaGrafico;
  const estreito = graficoEstreito(larguraGrafico);
  const paleta = usePaletaAtiva();
  const opcoesGrafico = useMemo(
    () => mesSelecionado && montaOpcoesPrevisoes(mesSelecionado, alturaGrafico, estreito, paleta),
    [mesSelecionado, alturaGrafico, estreito, paleta]
  );
  const chaveGrafico = mesSelecionado && `${mesSelecionado.rotulo}-${estreito}-${paleta.fundoCartao}`;

  const semMeses = meses.length === 0;
  const exportar = () => baixarCsv("previsoes", montarCsv(CABECALHO_PREVISOES_CSV, linhasPrevisoesCsv(meses)));

  return (
    <EstadoRequisicao carregando={carregando} erro={erro} vazio={vazio} onRecarregar={recarregar}>
      {dados && (
        <div
          ref={refCartao}
          style={{
            ...estilos.cartao,
            minHeight: alturaDisponivel || undefined,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid container spacing={4} alignItems={largo ? "stretch" : "flex-start"} style={{ padding: "clamp(16px, 2.5vw, 30px)", flex: 1, margin: 0, width: "100%" }}>
            <Grid item xs={12} md={4} xl={3}>
              <div style={estilosAba.tituloBloco}>Recebimentos</div>
              <div style={estilosAba.subtituloBloco}>previstos por mês</div>
              <div style={estilosAba.janela}>
                {filtro && textoJanelaPrevisoes(filtro.fim)}
              </div>

              {dados.mesCorrente && (
                <div style={{ marginBottom: 20 }}>
                  <CartaoResumo rotulo={dados.mesCorrente.rotulo} valor={dados.mesCorrente.total} />
                </div>
              )}

              <CartaoResumo
                rotulo={`Total previsto (${dados.meses.length} ${dados.meses.length === 1 ? "mês" : "meses"})`}
                valor={dados.totalPrevisto}
              />

              <Button
                onClick={exportar}
                disabled={semMeses}
                style={{
                  fontFamily: FONTE,
                  background: semMeses ? BORDA : NAVY_FUNDO,
                  color: semMeses ? CINZA_BOTAO : TEXTO_SOBRE_COR,
                  cursor: semMeses ? "not-allowed" : "pointer",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 14,
                  borderRadius: 24,
                  padding: "8px 26px",
                  boxShadow: "none",
                  whiteSpace: "nowrap",
                  marginTop: 20,
                }}
              >
                Exportar
              </Button>
            </Grid>

            <Grid item xs={12} md={8} xl={9} style={{ display: "flex", flexDirection: "column" }}>
              {mesSelecionado && (
                <div style={estiloPainelGrafico}>
                  <div role="tablist" aria-label="Mês da previsão" style={estiloAbasMes(colunasAbasMes(meses.length, larguraGrafico))}>
                    {meses.map((mes, indice) => {
                      const ativa = mes === mesSelecionado;
                      return (
                        <button
                          key={mes.rotulo}
                          type="button"
                          role="tab"
                          aria-selected={ativa}
                          style={estiloAbaMes(ativa)}
                          onClick={() => setIndiceMes(indice)}
                        >
                          {mes.rotulo}
                        </button>
                      );
                    })}
                  </div>

                  <TotalDoMes valor={mesSelecionado.total} />

                  {/* Remonta por mes e por layout: o update do Highcharts mescla as opcoes e nao desfaz as anteriores */}
                  {opcoesGrafico &&
                    (largo ? (
                      <div ref={refAreaGrafico} style={{ flex: 1, position: "relative", minHeight: alturaMinimaGrafico }}>
                        <div style={{ position: "absolute", top: 0, left: 0, right: 0 }}>
                          {larguraGrafico > 0 && <Grafico key={chaveGrafico} opcoes={opcoesGrafico} />}
                        </div>
                      </div>
                    ) : (
                      <div ref={refAreaGrafico}>
                        {larguraGrafico > 0 && <Grafico key={chaveGrafico} opcoes={opcoesGrafico} />}
                      </div>
                    ))}
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      )}
    </EstadoRequisicao>
  );
};

export default Previsoes;
